/* eslint-disable max-lines-per-function */
import classNames from 'classnames'
import { useState } from 'react'
import dynamic from 'next/dynamic'
import { StrapiV4Image } from '../../atoms-components/image/image'
import { TStrapiV4TitleSubTitleIconCards } from '../../common-types'
import { getColorHexFromDotBrandColorNotation } from '../../utils/colors'
import { StrapiV4RichTextEditor } from '../rich-text-editor/rich-text-editor'
import Title from '../../common-components/Title'
import Link from '@/components/Common/Link'
import colors from '@ind-money/tailwind/tokens/colors'
import { Strapi4CTAByType } from '@/stories/strapi-4/atoms-components/cta-by-type/cta-by-type'
import Text from '@/stories/components/Common/NewText'
import { GuideData1, MFGuideData1 } from '@/root/constants/blogs/constant'
import { useRouter } from 'next/router'

const NRIModal = dynamic(() => import('@/components/nri/NRIModal'))
const StrapiV4GuidesWidget = dynamic(
  () => import('../guides-widget/guides-widget'),
)
const Modal = dynamic(() => import('@/stories/components/Common/Modal'))

type TProps = Pick<
  TStrapiV4TitleSubTitleIconCards,
  'title' | 'bg_color' | 'sub_title' | 'section_bg_color' | 'ctas'
> & {
  cards: Pick<
    TStrapiV4TitleSubTitleIconCards,
    'three_col_cards'
  >['three_col_cards'] & { id?: number }
  notHero?: boolean
  addClassNames?: string | null
}

export function StrapiV4TitleSubTitleIconCards(props: TProps) {
  const router = useRouter()
  const {
    title,
    cards,
    bg_color,
    sub_title,
    section_bg_color,
    ctas,
    notHero,
    addClassNames,
  } = props

  const bgColor = section_bg_color?.color
    ? getColorHexFromDotBrandColorNotation(section_bg_color.color)
    : (section_bg_color?.custom_color ?? colors.brand.white)
  const [showModal, setShowModal] = useState(-1)

  return (
    <div
      className={classNames('pb-1 pt-1 lg:pb-10 lg:pt-10', {
        'my-8 xl:my-12 ': bgColor !== colors.brand.white,
        [addClassNames ?? '']: addClassNames,
      })}
      style={{
        backgroundColor: bgColor,
      }}
    >
      <div className={classNames({ 'herolike-wrapper': !notHero })}>
        <div className="pt-5">
          <Title
            fontSize={notHero ? 'size-34' : 'size-54'}
            headingType="h2"
            className={classNames(
              'mx-auto flex max-w-5xl flex-col pb-3 font-semibold text-brand-black',
              { 'mb-[2.5rem] lg:pb-7': !sub_title },
              { 'mt-[2.5rem]': bgColor !== colors.brand.white },
              { 'text-center': !notHero },
            )}
          >
            {title}
          </Title>
          <p
            className={classNames(
              'mx-auto max-w-4xl text-lg leading-7 text-brand-grey-dark  lg:leading-9',
              { 'text-center lg:text-2xl': !notHero },
            )}
          >
            {sub_title}
          </p>

          <ul className="my-[2.5rem] grid gap-6 lg:grid-cols-3">
            {cards.map((el, index) => {
              const list = (
                <li
                  onClick={() => {
                    if (el.link) {
                      router.push(el.link)
                    }
                  }}
                  key={index}
                  className={classNames(
                    'flex h-full flex-col rounded-3xl px-5 py-6',
                    {
                      'justify-between !px-6 !py-10':
                        el.variant === 'secondary',
                    },
                    {
                      'cursor-pointer transition-transform hover:scale-105':
                        el.link,
                    },
                  )}
                  style={{
                    backgroundColor: bg_color?.color
                      ? getColorHexFromDotBrandColorNotation(bg_color.color)
                      : (bg_color?.custom_color ?? '#ffffff'),
                  }}
                >
                  {el.icon?.img_url && el.variant !== 'secondary' && (
                    <div className="mb-5 h-12 w-12 lg:h-18 lg:w-18">
                      <StrapiV4Image
                        alt={el.icon.alt}
                        img={el.icon.img_url}
                        width={el.icon.width}
                        height={el.icon.height}
                        verticalBuffer={false}
                      />
                    </div>
                  )}
                  <div>
                    {el.title && (
                      <p
                        className={classNames(
                          'mb-5 text-2xl font-medium text-brand-black',
                          {
                            '!mb-0 !text-[18px] !font-normal !text-brand-grey-base':
                              el.variant === 'secondary',
                          },
                          { '!mb-0': el.rich_text_html?.rich_text_html },
                        )}
                      >
                        {el.link && el.variant !== 'secondary' ? (
                          <Link href={el.link}>{el.title}</Link>
                        ) : (
                          el.title
                        )}
                      </p>
                    )}
                    {el.sub_title &&
                      (el.variant === 'secondary' ? (
                        <h3
                          className={classNames(
                            'text-2xl font-medium text-brand-black',
                          )}
                        >
                          {el.link ? (
                            <Link href={el.link}>{el.sub_title}</Link>
                          ) : (
                            el.sub_title
                          )}
                        </h3>
                      ) : (
                        <p
                          className={classNames(
                            'leading-6 text-brand-grey-base',
                          )}
                        >
                          {el.sub_title}
                        </p>
                      ))}
                  </div>
                  {el.rich_text_html?.rich_text_html && (
                    <StrapiV4RichTextEditor
                      content={el.rich_text_html.rich_text_html}
                      className={classNames(
                        'text-base leading-6 text-brand-grey-base',
                        {
                          '!text-2xl font-medium !text-brand-black':
                            el.variant === 'secondary',
                        },
                      )}
                    />
                  )}
                  <div>
                    {el.icon?.img_url && el.variant === 'secondary' && (
                      <div className="mt-5">
                        <StrapiV4Image
                          alt={el.icon.alt}
                          img={el.icon.img_url}
                          width={el.icon.width}
                          height={el.icon.height}
                          verticalBuffer={false}
                        />
                      </div>
                    )}
                    {el.cta?.text && (
                      <Text
                        onClick={() => {
                          setShowModal(index)
                        }}
                        appearance="subtitle1"
                        addClassName="text-brand-blue-azureRadiance text-center mt-4 cursor-pointer"
                      >
                        {el.cta.text}
                      </Text>
                    )}
                  </div>
                  {el.popup_type &&
                    ['USGuide1', 'MFGuide1', 'NRIModal'].includes(
                      el.popup_type,
                    ) && (
                      <Modal
                        showModal={index === showModal}
                        bodyClassName="max-w-6xl w-full !px-0"
                        closeUIClassName="!right-2 !md:top-2 !top-1"
                        onClose={() => setShowModal(-1)}
                        modalWrapperClass="!items-start animate-ModalB2T md:top-18 top-[30%]"
                        fullScreenOnMobile
                      >
                        {el.popup_type === 'NRIModal' ? (
                          <NRIModal />
                        ) : (
                          <StrapiV4GuidesWidget
                            __component="widgets.guides-widget"
                            showSteps={true}
                            guide_data={
                              el.popup_type === 'USGuide1'
                                ? GuideData1
                                : MFGuideData1
                            }
                            selectedStep={index}
                            notHero
                          />
                        )}
                      </Modal>
                    )}
                </li>
              )
              return list
            })}
          </ul>
          {!!ctas?.text && (
            <Strapi4CTAByType
              type={ctas.type}
              url={ctas.url}
              addClassNames="mx-auto"
            >
              {ctas.text}
            </Strapi4CTAByType>
          )}
        </div>
      </div>
    </div>
  )
}
